import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { T } from './theme';

// THE MISS LEDGER (Chad ruling 2026-09-20): what was not done on time, with the date, time, and who owned it.
// Facility mode: 24h/7d by source and by owner, supervisor acknowledge + reason. Episode mode: this client's rows.
const API = 'https://tcio-api.onrender.com';
const hdrs = () => ({ headers: { Authorization: 'Bearer ' + localStorage.getItem('tcio_token') } });
const fmtDT = (v) => v ? new Date(v).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';
const SRC = { rounds: { l: 'Round', c: '#b45309' }, vitals: { l: 'Vitals', c: '#2563eb' }, note: { l: 'Note', c: '#7c3aed' }, med: { l: 'Med', c: '#dc2626' } };
const ACK_ROLES = ['ceo', 'admin', 'org_admin', 'operations', 'program_manager', 'clinical_director', 'nursing_supervisor', 'medical', 'ur_supervisor'];

export default function MissesPanel({ facilityId, episodeId, days: days0, compact, role }) {
  const [days, setDays] = useState(days0 || (episodeId ? 7 : 1));
  const [rows, setRows] = useState([]); const [meds, setMeds] = useState([]);
  const [summary, setSummary] = useState(null); const [busy, setBusy] = useState(null);
  const canAck = ACK_ROLES.includes(role || localStorage.getItem('tcio_role') || '');
  const load = useCallback(async () => {
    const key = episodeId ? 'episodeId=' + episodeId : 'facilityId=' + facilityId;
    if (!episodeId && !facilityId) return;
    try {
      const r = await axios.get(`${API}/api/compliance/misses?${key}&days=${days}`, hdrs());
      setRows(r.data.misses || []); setMeds(r.data.med_misses || []);
      if (!episodeId) { const s = await axios.get(`${API}/api/compliance/misses/summary?facilityId=${facilityId}`, hdrs()); setSummary(s.data); }
    } catch (e) { /* silent */ }
  }, [facilityId, episodeId, days]);
  useEffect(() => { load(); }, [load]);
  const ack = async (m) => {
    const reason = window.prompt('Reason / finding for this miss (' + (SRC[m.source] || {}).l + ' ' + fmtDT(m.due_at) + '):');
    if (!reason || reason.trim().length < 3) return;
    setBusy(m.id);
    try { await axios.post(`${API}/api/compliance/misses/${m.id}/ack`, { reason: reason.trim() }, hdrs()); await load(); }
    catch (e) { alert((e.response && e.response.data && e.response.data.error) || 'Failed'); }
    setBusy(null);
  };
  const total = rows.length + meds.length;
  const pill = (s) => <span style={{ background: '#fff', border: '1px solid ' + (SRC[s] || {}).c, color: (SRC[s] || {}).c, borderRadius: 99, padding: '1px 8px', fontSize: 11, fontWeight: 800 }}>{(SRC[s] || { l: s }).l}</span>;
  return (
    <div style={{ background: T.surface, border: `1px solid ${T.b200}`, borderRadius: 10, padding: compact ? 10 : 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: compact ? 12.5 : 14 }}>Misses</strong>
        <span style={{ fontSize: 12, color: T.inkSoft }}>not done on time — timestamped, owned, never a live alarm</span>
        <span style={{ flex: 1 }} />
        {[1, 7, 30].map(d => <button key={d} onClick={() => setDays(d)} style={{ padding: '3px 9px', borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
          border: '1px solid ' + (days === d ? T.rose : T.b300), background: days === d ? T.rose : '#fff', color: days === d ? '#fff' : T.ink }}>{d === 1 ? '24h' : d + 'd'}</button>)}
      </div>
      {summary && summary.by_source && summary.by_source.length > 0 && (
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12, marginBottom: 8 }}>
          {summary.by_source.map(s => <span key={s.source}>{pill(s.source)} <b>{s.last_24h}</b> / 24h · <b>{s.last_7d}</b> / 7d · {s.unacknowledged_7d} unacknowledged</span>)}
          {summary.by_owner_7d && summary.by_owner_7d.length > 0 && <span style={{ color: T.inkSoft }}>Owners 7d: {summary.by_owner_7d.slice(0, 4).map(o => (o.owner_name || 'no one on shift') + ' (' + o.n + ')').join(', ')}</span>}
        </div>
      )}
      {total === 0 ? (
        <div style={{ fontSize: 12.5, color: T.inkSoft, padding: '6px 0' }}>Nothing missed in this window.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead><tr style={{ textAlign: 'left', color: T.inkSoft, fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1 }}>
            <th style={{ padding: 4 }}>Due</th><th>What</th>{!episodeId && <th>Client</th>}<th>Owner</th><th>Outcome</th><th></th>
          </tr></thead>
          <tbody>
            {rows.slice(0, compact ? 6 : 200).map(m => (
              <tr key={m.id} style={{ borderTop: `1px solid ${T.b100}` }}>
                <td style={{ padding: 4, whiteSpace: 'nowrap' }}>{fmtDT(m.due_at)}</td>
                <td>{pill(m.source)} <span style={{ color: T.inkSoft }}>{m.level_code || (m.interval_minutes ? 'q' + (m.interval_minutes >= 60 ? m.interval_minutes / 60 + 'h' : m.interval_minutes + 'm') : m.rule_key)}{m.house_name ? ' · ' + m.house_name : ''}</span></td>
                {!episodeId && <td>{m.patient_name || '—'}</td>}
                <td>{m.owner_name || <span style={{ color: T.inkSoft }}>no one on shift</span>}</td>
                <td>{m.done_late_at ? <span style={{ color: '#b45309', fontWeight: 700 }}>done {Math.round((new Date(m.done_late_at) - new Date(m.due_at)) / 60000)} min late</span> : <span style={{ color: '#dc2626', fontWeight: 700 }}>missed</span>}
                  {m.acknowledged_at && <div style={{ fontSize: 11, color: T.inkSoft }}>ack {m.acknowledged_by_name || ''} · {m.ack_reason}</div>}</td>
                <td style={{ textAlign: 'right' }}>{canAck && !m.acknowledged_at && <button disabled={busy === m.id} onClick={() => ack(m)}
                  style={{ padding: '3px 8px', fontSize: 11, borderRadius: 6, border: `1px solid ${T.b300}`, background: '#fff', cursor: 'pointer' }}>{busy === m.id ? '…' : 'Acknowledge'}</button>}</td>
              </tr>
            ))}
            {meds.slice(0, compact ? 4 : 100).map(x => (
              <tr key={'med' + x.id} style={{ borderTop: `1px solid ${T.b100}` }}>
                <td style={{ padding: 4, whiteSpace: 'nowrap' }}>{String(x.scheduled_date).slice(0, 10)} {String(x.scheduled_time || '').slice(0, 5)}</td>
                <td>{pill('med')} <span style={{ color: T.inkSoft }}>{x.medication_name}</span></td>
                {!episodeId && <td>—</td>}
                <td><span style={{ color: T.inkSoft }}>per MAR</span></td>
                <td><span style={{ color: '#dc2626', fontWeight: 700 }}>missed</span></td>
                <td></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
